import { useCallback, useEffect, useState } from 'react';
import { Button, Link } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { TrialsService } from '../../services/TrialServices';
import CallButton from '../Common/CallButton/CallButton';
import { UserType } from '../../enums/common';
import moment from 'moment';

interface InputProps {
    houseId: number;
}

const trialsService = new TrialsService();

const HouseTrialTab = ({ houseId }: InputProps) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const refreshData = useCallback(() => {
        setLoading(true);
        trialsService
            .getTrialsForHouse(houseId, rowsPerPage, page * rowsPerPage, { order: 'DESC', field: 'id' })
            .then((res) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [houseId, page, rowsPerPage]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const columns: CommonListColumn[] = [
        { label: 'Id', field: 'id' },
        { label: 'Cook Name', field: 'cookName' },
        { label: 'CX Name', field: 'customerName' },
        {
            label: 'CX Mobile',
            stopRowClick: true,
            render: (row) => (
                <CallButton toPhoneNumber={row?.customerPhoneNumber} userType={UserType.CUSTOMER} />
            ),
        },
        { label: 'Status', field: 'status', type: 'status' },
        { label: 'Subscription Price', field: 'totalPrice' },
        {
            label: 'House',
            render: (row) => (
                <Link href={`/#/Houses/${row?.houseId}/show`} underline="hover">{row?.houseId}</Link>
            ),
        },
        {
            label: 'Address',
            minWidth: 200,
            render: (row) => [row?.house?.locality, row?.house?.sector, row?.house?.address, row?.house?.city]
                .filter(Boolean).join(', ') || '-',
        },
        {
            label: 'Date',
            render: (row) => row?.date ? moment(row?.date).format('DD MMM YYYY') : '-',
        },
        { label: 'Time', field: 'time' },
        {
            label: 'PR',
            render: (row) => row?.prId ? (
                <Link href={`/#/PermanentReplacement/${row.prId}/show`} underline="hover">{row.prId}</Link>
            ) : '-',
        },
        {
            label: 'Meal Mapping',
            render: (row) => row?.mealMapping?.length
                ? row.mealMapping.map((m: any) => `${m.slot}: ${m.time}`).join(', ')
                : '---',
        },
        { label: 'Trial Owner', field: 'trialOwnerName' },
        { label: 'Trial Owner Email', field: 'trialOwnerEmail' },
        { label: 'Customer Feedback', field: 'customerFeedback' },
        { label: 'Cancel Reason', field: 'cancellationReason' },
        { label: 'Cancel Sub Reason', field: 'cancellationSubReason' },
        { label: 'Reschedule Reason', field: 'rescheduleReason' },
        { label: 'Reschedule Sub Reason', field: 'rescheduleSubReason' },
        { label: 'Cook Rejection Reason', field: 'cookRejectionReason' },
        {
            label: 'Actions',
            stopRowClick: true,
            render: (row) => (
                <Link href={`/#/Trials/${row.id}/show`}>
                    <Button variant="contained" color="primary" size="small">Show</Button>
                </Link>
            ),
        },
    ];

    return (
        <CommonListTable
            columns={columns}
            data={data}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            emptyText="No Trials Found"
        />
    );
};

export default HouseTrialTab;
